import React, { useEffect, useState } from 'react'
import { Alert, AlertDescription, AlertIcon, Link, Text } from '@chakra-ui/react'

import type { Agreement } from '@homevest/types/rental-applications'

import { useApplicationBuildiumLeaseSyncSubscription } from 'graphql/generated'
import { getLeaseSummaryUrl } from 'constants/buildium'

export const BUILDIUM_LEASE_SYNC_GRACE_PERIOD_MS = 60_000
export const BUILDIUM_LEASE_MISSING_WARNING_DELAY_MS = 5 * 60_000

export type BuildiumLeaseAlertState =
  | 'hidden'
  | 'missing-agreement'
  | 'missing-lease'
  | 'syncing'
  | 'overdue'

type BuildiumLeaseSyncSnapshot = Pick<Agreement, 'id' | 'created_at' | 'buildium_lease_id'>

const agreementCreatedAtMs = (createdAt: Date | string | null): number | null => {
  if (!createdAt) {
    return null
  }

  const timestamp = new Date(createdAt).getTime()
  return Number.isFinite(timestamp) ? timestamp : null
}

export const isBuildiumLeaseSyncOverdue = (
  createdAt: Date | string | null,
  now = Date.now()
): boolean => {
  const createdAtMs = agreementCreatedAtMs(createdAt)
  return createdAtMs === null || now - createdAtMs >= BUILDIUM_LEASE_SYNC_GRACE_PERIOD_MS
}

export const isBuildiumLeaseMissing = (
  createdAt: Date | string | null,
  now = Date.now()
): boolean => {
  const createdAtMs = agreementCreatedAtMs(createdAt)
  return createdAtMs === null || now - createdAtMs >= BUILDIUM_LEASE_MISSING_WARNING_DELAY_MS
}

export const getBuildiumLeaseAlertState = ({
  isPostLeaseCreation,
  agreementExists,
  buildiumLeaseId,
  isOverdue,
  isMissingLease
}: {
  isPostLeaseCreation: boolean
  agreementExists: boolean
  buildiumLeaseId: number | null
  isOverdue: boolean
  isMissingLease: boolean
}): BuildiumLeaseAlertState => {
  if (!isPostLeaseCreation || buildiumLeaseId !== null) {
    return 'hidden'
  }
  if (!agreementExists) {
    return 'missing-agreement'
  }
  if (isMissingLease) {
    return 'missing-lease'
  }
  return isOverdue ? 'overdue' : 'syncing'
}

export const useBuildiumLeaseSync = (
  rentalApplicationId: string,
  fallbackAgreement?: BuildiumLeaseSyncSnapshot
) => {
  const [{ data }] = useApplicationBuildiumLeaseSyncSubscription({
    variables: { rentalApplicationId }
  })
  const [, forceDeadlineRender] = useState(0)

  const subscriptionHasResponded = data !== undefined
  const subscribedAgreement = data?.rental_applications_by_pk?.agreements[0]
  const agreement = subscriptionHasResponded ? subscribedAgreement : fallbackAgreement
  const buildiumLeaseId = agreement?.buildium_lease_id ?? null
  const createdAt = agreement?.created_at ?? null
  const isOverdue = isBuildiumLeaseSyncOverdue(createdAt)
  const isMissingLease = isBuildiumLeaseMissing(createdAt)

  useEffect(() => {
    if (!agreement || buildiumLeaseId !== null || isMissingLease) {
      return
    }

    const createdAtMs = agreementCreatedAtMs(createdAt)
    if (createdAtMs === null) {
      return
    }

    const nextDelayMs = isOverdue
      ? BUILDIUM_LEASE_MISSING_WARNING_DELAY_MS
      : BUILDIUM_LEASE_SYNC_GRACE_PERIOD_MS
    const remainingMs = Math.max(0, createdAtMs + nextDelayMs - Date.now())
    const timeoutId = window.setTimeout(
      () => forceDeadlineRender((renderCount) => renderCount + 1),
      remainingMs
    )

    return () => window.clearTimeout(timeoutId)
  }, [agreement, buildiumLeaseId, createdAt, isMissingLease, isOverdue])

  return {
    agreementExists: Boolean(agreement),
    buildiumLeaseId,
    isOverdue,
    isMissingLease
  }
}

export const BuildiumLeaseSyncAlert = ({ state }: { state: BuildiumLeaseAlertState }) => {
  if (state === 'hidden') {
    return null
  }

  const isSyncing = state === 'syncing'
  const description = isSyncing
    ? 'Lease created. Buildium sync is in progress. This page will update automatically.'
    : state === 'overdue'
    ? 'The Buildium lease is taking longer than expected to sync. Refresh the page to check again.'
    : state === 'missing-lease'
    ? 'This application is past lease creation, but no Buildium lease is associated with its agreement.'
    : 'This application is past lease creation, but no active lease agreement was found.'

  return (
    <Alert status={isSyncing ? 'info' : 'warning'} alignItems='start' marginBottom={4}>
      <AlertIcon />
      <AlertDescription>{description}</AlertDescription>
    </Alert>
  )
}

export const BuildiumLeaseValue = ({
  buildiumLeaseId,
  alertState
}: {
  buildiumLeaseId: number | null
  alertState: BuildiumLeaseAlertState
}) => {
  if (buildiumLeaseId !== null) {
    return (
      <Link color='blue.500' href={getLeaseSummaryUrl(buildiumLeaseId)} isExternal={true}>
        {buildiumLeaseId}
      </Link>
    )
  }

  return <Text marginTop={0}>{alertState === 'syncing' ? 'Syncing…' : 'No Buildium lease'}</Text>
}
